import { createContext, useContext, useState, useEffect, ReactNode, FC } from 'react';
import api from '../services/api';
import {StatusType} from "../types";
import {useAuth} from "./AuthContext";

export interface Category {
    id: number;
    name: string;
}

export interface Client {
    id: number;
    name: string;
}

interface DataContextType {
    categories: Category[];
    clients: Client[];
    isLoading: boolean;
    error: string | null;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [status, setStatus] = useState<StatusType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const {user} = useAuth()

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        fetchData()
    }, [user]);

    const fetchData = async () => {
        if(user === null || user === undefined)
            return
        setIsLoading(true);
        setError(null);
        try {
            const promises = [
                api.get('/api/categories/getAll'),
                api.get('/api/status/getAll')
            ];

            if (!user.client) {
                promises.push(api.get('/api/clients/getAllNoPage'));
            }

            const [categoriesRes, statusRes, clientsRes] = await Promise.all(promises);

            if (categoriesRes.data.status) {
                setCategories(categoriesRes.data.data);
            } else {
                throw new Error('Impossibile caricare le categorie');
            }

            if (statusRes.data.status) {
                setStatus(statusRes.data.data);
            } else {
                throw new Error('Impossibile caricare gli stati');
            }

            if (!user.client) {
                if (clientsRes.data.status) {
                    setClients(clientsRes.data.data);
                } else {
                    throw new Error('Impossibile caricare i clienti');
                }
            }

        } catch (err: any) {
            setError(err.message || "Si è verificato un errore durante il caricamento dei dati.");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const value = { categories, clients, isLoading, error };

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
};

export const useData = () => {
    const context = useContext(DataContext);
    if (context === undefined) {
        throw new Error('useData deve essere usato all\'interno di un DataProvider');
    }
    return context;
};
